import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect
} from "react";
import PropTypes from "prop-types";
import { Accordion, useAccordionButton } from "react-bootstrap";
import { Alert } from "../../../../stories/Alerts/Alert";
import { MarkerScaleGraph } from "../../Reports/MarkerScale/MarkerScaleGraph";
import MarkerGraph from "../../Reports/MarkerScale/MarkerGraph";
import BackButton from "../../../.././assets/icons/back_button.svg";
import { barcodeDataApi } from "../../../../utils/constants";
import useApis from "../../../../services/useAPI";
import { useMediaQuery } from "react-responsive";
import successIcon from "../../../.././assets/icons/Step_success.svg"

/**
 * Hidden trigger so we can programmatically open this accordion from a ref.
 */
function ProgrammaticToggle({ eventKey, onToggle }) {
  const handleClick = useAccordionButton(eventKey, onToggle);
  return (
    <span
      ref={onToggle.__ref__}
      onClick={handleClick}
      style={{ display: "none" }}
    />
  );
}

/**
 * NavItem
 *
 * Accordion body has two views:
 *   1. LIST   — item.content() — all marker cards with "See details" buttons
 *   2. DETAIL — MarkerScaleGraph for a specific marker
 *
 * The accordion header title swaps between item.label and the marker name.
 */
const NavItem = forwardRef(function NavItem(
  {
    item,
    index,
    isCurrent,
    setCurrentItem,
    getStatusIcon,
    status,
    isPartial,
    reportedDate,
    activeMarkerDetail = null,  
    onMarkerDetailOpen,          
    onMarkerDetailClose, 
  },
  ref,
) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const isOpenRef = useRef(false);
  const { callApi, resultGet, resultPost, serverError, isLoading } = useApis();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // null = list view, marker object = detail view
  const [selectedMarker, setSelectedMarker] = useState(null);

  useImperativeHandle(ref, () => ({
    open() {
      if (!isOpenRef.current) triggerRef.current?.click();
      setTimeout(() => {
        wrapperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);
    },

    openMarkerDetail(marker) {
      setSelectedMarker(marker);
      if (!isOpenRef.current) triggerRef.current?.click();
      setTimeout(() => {
        wrapperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);
    },

    reset() {
      setSelectedMarker(null);
    },
  }));

  const onToggle = Object.assign(
    () => {
      isOpenRef.current = !isOpenRef.current;
      setCurrentItem(item.name);
    },
    { __ref__: triggerRef },
  );

  const getStatusLabel = (status) => {
      const statusString = String(status);
    if (statusString === "good") return "Everything looks good";
    if (statusString === "require-attention") {
    return "Some results require attention";
}
    return "Some borderline results";
  };

  useEffect(() => {
    if (activeMarkerDetail && activeMarkerDetail !== selectedMarker) {
      setSelectedMarker(activeMarkerDetail);
    }
  }, [activeMarkerDetail]);

  const handleBack = () => {
    setSelectedMarker(null);
    onMarkerDetailClose?.();
  };
  const headerTitle = selectedMarker ? selectedMarker.name : item.label;

  const openMarkerDetail = (marker) => {
    setSelectedMarker(marker);
    onMarkerDetailOpen?.(marker);
    if (!isOpenRef.current) triggerRef.current?.click();
    setTimeout(() => {
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  useEffect(() => {
    if (selectedMarker) {
      if (selectedMarker.code === "HbA1c") {
        selectedMarker.code = "HBA1c";
      }
      callApi(
        null,
        barcodeDataApi(selectedMarker.code),
        "GET",
       );
     }
   }, [selectedMarker]);

   if (serverError) {
      return( <p className="text-muted">
        {serverError}
      </p>);
    }

  return (
    <div ref={wrapperRef} id={`nav-item-${item.name}`} className={`${isMobile && "mobile-marker-graph"}`}>
      {selectedMarker ? (
        <>
          <div className={`horizontal-section-nav-button-label d-flex gap-2 ${isMobile && "mobile-scale-graph-header"}`}>
            <div className={`d-flex align-items-center ${isMobile && "px-2"}`}>
              <button
                className="navitem__back-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBack();
                }}
                aria-label="Back to list"
              >
                <img src={BackButton} alt="back button" />
              </button>
              <span className="horizontal-section-biomarker-title">
                {isMobile && (
                  <img src={successIcon} alt="success icon" className="me-2"/>
                )}
               {`${headerTitle}`}
              </span>
            </div>
          </div>
          <MarkerScaleGraph
            marker={selectedMarker}
            isMobile={isMobile}
            graphContent={
              <MarkerGraph
                marker={selectedMarker}
                reportedDate={reportedDate}
                currentValue={selectedMarker.value}
                historicalData={resultGet?.data || []}
              />
            }
          />
        </>
      ) : (
        <Accordion
          defaultActiveKey={!isMobile && index === 0 ? "0" : null}
          className="horizontal-section-nav-button p-0 m-0 w-100 border-0 horizontal-section-nav-button--current"
          style={{
            backgroundColor: item.background,
            borderColor: !isCurrent ? item.background : undefined,
          }}
        >
          <Accordion.Item
            eventKey="0"
            className="border-0 bg-transparent w-100"
          >
            <Accordion.Header
              onClick={() => {
                isOpenRef.current = !isOpenRef.current;
                setCurrentItem(item.name);
              }}
            >
              <div className="horizontal-section-nav-button-label d-flex gap-2">
                <span className="horizontal-section-nav-button-title">
                  {headerTitle}
                </span>
                {!selectedMarker && item.subtitle && (
                  <span className="horizontal-section-nav-button-subtitle">
                    {item.subtitle}
                  </span>
                )}
                {!isPartial && (
                  <p
                    className={`healthArea__header-subtitle healthArea__header-mobile-subtitle healthArea__header-subtitle--${status} mb-0`}
                  >
                    <span>{getStatusLabel(status)}</span>
                  </p>
                )}
              </div>
              <div className="healthArea__header-content">
                {!isPartial && (
                  <p
                    className={`healthArea__header-subtitle healthArea__header-subtitle--${status}`}
                  >
                    <span>{getStatusLabel(status)}</span>
                  </p>
                )}
              </div>
            </Accordion.Header>
            {selectedMarker && <hr />}

            <ProgrammaticToggle eventKey="0" onToggle={onToggle} />

            {/* ── Body ── */}
            <Accordion.Body className="px-0 pt-2 pb-0">
              <main className="horizontal-section-main p-0">
                <hr />
                {item.area.area === "Full blood count" && isPartial ? "" : item.content()}
                {isPartial?.includes(item.name) && (
                  <div className="healthArea__container">
                    <div className="healthArea__list">
                      <Alert
                        className="partial-alert"
                        variant="light"
                        text={`**${item.name}** could not be reported.`}
                      />
                    </div>
                  </div>
                )}
              </main>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
});

NavItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    content: PropTypes.func.isRequired,
    background: PropTypes.string,
    subtitle: PropTypes.string,
    status: PropTypes.string,
    area_status: PropTypes.string,
    hiddenFromNav: PropTypes.bool,
  }).isRequired,
  isCurrent: PropTypes.bool.isRequired,
  setCurrentItem: PropTypes.func.isRequired,
  getStatusIcon: PropTypes.func.isRequired,
  status: PropTypes.string,
  isPartial: PropTypes.array,
};

export default NavItem;
